import { useState, useEffect } from 'react';
import { showAlert, showConfirm } from '@/components/AppDialog';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Trash2, CreditCard, X, ChevronLeft, ReceiptText, Lock, Check } from 'lucide-react';
import { API_BASE_URL } from '@/utils/env';
import AppShell, { ACCENT, ACCENT_DEEP } from '../components/AppShell';
import DeleteAccountModal from '../components/DeleteAccountModal';

interface CardData { brand: string; last4: string; expMonth: string; expYear: string; holder: string; }
type Panel = null | 'invoices' | 'payment';

function detectBrand(num: string) {
  if (/^4/.test(num)) return 'Visa';
  if (/^5[1-5]/.test(num)) return 'Mastercard';
  if (/^3[47]/.test(num)) return 'Amex';
  return '';
}

export default function Settings() {
  const navigate = useNavigate();

  // Auth
  const [uid, setUid] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [fetching, setFetching] = useState(true);

  // Password
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // Payment
  const [card, setCard] = useState<CardData | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [cardNum, setCardNum] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [savingCard, setSavingCard] = useState(false);
  const [cardSaved, setCardSaved] = useState(false);

  // Delete
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) { navigate('/login'); return; }
        const user = JSON.parse(userStr);
        setUid(user.uid);
        const tokensStr = localStorage.getItem('authTokens');
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
          headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) }
        });
        if (resp.ok) {
          const json = await resp.json();
          if (json.success && json.user) {
            setUserEmail(json.user.email || '');
            if (json.user.paymentCard) setCard(json.user.paymentCard);
          }
        }
      } catch (e) { console.error(e); }
      finally { setFetching(false); }
    })();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('authTokens');
    localStorage.removeItem('token');
    navigate('/');
  };

  const handleSavePassword = async () => {
    if (!oldPassword || !newPassword) { setPwMsg({ type: 'err', text: 'Please fill in both password fields.' }); return; }
    if (newPassword.length < 6) { setPwMsg({ type: 'err', text: 'New password must be at least 6 characters.' }); return; }
    setChangingPw(true);
    setPwMsg(null);
    try {
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
      const resp = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: JSON.stringify({ email: userEmail, oldPassword, newPassword })
      });
      const data = await resp.json();
      if (resp.ok) {
        setPwMsg({ type: 'ok', text: 'Password updated. Signing you out…' });
        setOldPassword(''); setNewPassword('');
        setTimeout(() => handleLogout(), 2000);
      } else {
        setPwMsg({ type: 'err', text: data.error || 'Failed to update password.' });
      }
    } catch {
      setPwMsg({ type: 'err', text: 'Something went wrong. Please try again.' });
    } finally {
      setChangingPw(false);
    }
  };

  const handleSaveCard = async () => {
    if (!cardNum || cardNum.replace(/\D/g, '').length < 12) {
      showAlert('Please enter a valid card number.');
      return;
    }
    setSavingCard(true);
    try {
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
      const digits = cardNum.replace(/\D/g, '');
      const newCard: CardData = {
        brand: detectBrand(digits),
        last4: digits.slice(-4),
        expMonth: cardExpiry.split('/')[0] || '',
        expYear: cardExpiry.split('/')[1] || '',
        holder: cardHolder,
      };
      await fetch(`${API_BASE_URL}/auth/user/${uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: JSON.stringify({ paymentCard: newCard })
      });
      setCard(newCard);
      setCardSaved(true);
      setTimeout(() => { setCardSaved(false); setPanel(null); }, 1500);
    } catch (e) {
      console.error(e);
      showAlert('Failed to save card. Please try again.');
    } finally {
      setSavingCard(false);
    }
  };

  const handleRemoveCard = async () => {
    if (!(await showConfirm('Remove this payment method?', { destructive: true, confirmText: 'Remove' }))) return;
    try {
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
      await fetch(`${API_BASE_URL}/auth/user/${uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: JSON.stringify({ paymentCard: null })
      });
      setCard(null);
    } catch (e) { console.error(e); }
  };

  const handleDeleteAccount = async (password: string, reason: string) => {
    const tokensStr = localStorage.getItem('authTokens');
    const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
    const resp = await fetch(`${API_BASE_URL}/auth/delete-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
      body: JSON.stringify({ email: userEmail, password, reason })
    });
    const data = await resp.json();
    if (resp.ok) {
      setDeleteModalOpen(false);
      setTimeout(() => { localStorage.clear(); navigate('/login'); }, 1500);
    } else {
      throw new Error(data.error || 'Failed to delete account');
    }
  };

  const inputCls = "w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none";

  if (fetching) return <AppShell title="Settings" crumb="Settings" onLogout={handleLogout} loading />;

  return (
    <AppShell
      title="Settings"
      subtitle="Password, billing and account controls."
      crumb="Settings"
      onLogout={handleLogout}
    >
      <div className="pb-8">

          {/* Password */}
          <div className="bg-white rounded-xl border border-gray-200 mb-4">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="text-sm font-semibold text-[#111827]">Password</div>
              <div className="text-xs text-[#6B7280]">Keep your account secure</div>
            </div>
            <div className="px-6 py-6">
              {pwMsg && (
                <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${pwMsg.type === 'ok' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {pwMsg.text}
                </div>
              )}
              {/* Two of three columns — the pair reads as a pair, and neither
                  field stretches across the full-width card. */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">Current password</label>
                  <div className="relative">
                    <input type={showOld ? 'text' : 'password'} value={oldPassword} onChange={e => setOldPassword(e.target.value)} placeholder="Current password" className={`${inputCls} pr-10`} />
                    <button type="button" onClick={() => setShowOld(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">New password</label>
                  <div className="relative">
                    <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 6 characters" className={`${inputCls} pr-10`} />
                    <button type="button" onClick={() => setShowNew(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <button onClick={handleSavePassword} disabled={changingPw}
                  className="px-5 py-2 text-sm font-medium text-white rounded-lg transition disabled:opacity-50"
                  style={{ background: ACCENT }}
                  onMouseEnter={e => { e.currentTarget.style.background = ACCENT_DEEP; }}
                  onMouseLeave={e => { e.currentTarget.style.background = ACCENT; }}>
                  {changingPw ? 'Updating…' : 'Update password'}
                </button>
              </div>
            </div>
          </div>

          {/* Payment & billing */}
          <div className="bg-white rounded-xl border border-gray-200 mb-4">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-[#111827]">Payment & billing</div>
                <div className="text-xs text-[#6B7280]">Your plan & payment method · billed in dirhams</div>
              </div>
            </div>
            <div className="px-6 py-6">
              {!card ? (
                /* Empty state */
                <div className="text-center py-8">
                  <CreditCard className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-[#111827] mb-1">No payment method yet</p>
                  <p className="text-xs text-[#6B7280] mb-5 max-w-xs mx-auto">Add a card to activate billing. You'll only be charged when you start a paid plan.</p>
                  <button onClick={() => { setPanel('payment'); setCardNum(''); setCardHolder(''); setCardExpiry(''); }}
                    className="px-5 py-2 text-sm font-medium text-white rounded-lg transition"
                    style={{ background: ACCENT }}
                    onMouseEnter={e => { e.currentTarget.style.background = ACCENT_DEEP; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ACCENT; }}>
                    + Add payment method
                  </button>
                </div>
              ) : (
                /* Filled state */
                <div className="flex flex-col sm:flex-row gap-6">
                  {/* Card visual */}
                  <div className="rounded-2xl p-5 text-white w-full sm:w-56 flex-shrink-0 relative overflow-hidden"
                    style={{ background: 'linear-gradient(135deg, #1565c0, #7b1fa2)' }}>
                    <div className="text-xs font-bold tracking-widest mb-6 uppercase opacity-70">{card.brand}</div>
                    <div className="font-mono text-sm tracking-widest mb-4">•••• •••• •••• {card.last4}</div>
                    <div className="flex justify-between text-xs opacity-80">
                      <span>{card.holder || 'Cardholder'}</span>
                      <span>{card.expMonth}/{card.expYear}</span>
                    </div>
                  </div>
                  {/* Billing info */}
                  <div className="flex-1 text-sm space-y-2">
                    <div className="flex justify-between border-b border-gray-100 pb-2">
                      <span className="text-[#6B7280]">Card on file</span>
                      <span className="font-medium">{card.brand} ···· {card.last4}</span>
                    </div>
                    <div className="pt-3 flex flex-col gap-1.5">
                      <button onClick={() => setPanel('invoices')} className="acc-text text-xs hover:underline text-left">View invoice history</button>
                      <button onClick={() => { setPanel('payment'); setCardNum(''); setCardHolder(''); setCardExpiry(''); }} className="acc-text text-xs hover:underline text-left">Change payment method</button>
                      <button onClick={handleRemoveCard} className="text-red-500 text-xs hover:underline text-left">Remove card</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Danger zone */}
          <div className="bg-white rounded-xl border border-red-100">
            <div className="px-6 py-4 border-b border-red-100">
              <div className="text-sm font-semibold text-red-600">Danger zone</div>
            </div>
            <div className="px-6 py-6">
              <p className="text-sm text-[#6B7280] mb-5">Permanently remove your account and data. This cannot be undone.</p>
              <button onClick={() => setDeleteModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition">
                <Trash2 className="w-4 h-4" />
                Delete account
              </button>
            </div>
          </div>
      </div>

        {/* Invoice history panel */}
        {panel === 'invoices' && (
          <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black/30" onClick={() => setPanel(null)} />
            <div className="w-full max-w-sm bg-white shadow-2xl flex flex-col">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <button onClick={() => setPanel(null)} className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-gray-900">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={() => setPanel(null)}><X className="w-4 h-4 text-gray-400" /></button>
              </div>
              <div className="px-6 py-5 flex-1">
                <div className="text-sm font-semibold text-[#111827] mb-1">Invoice history</div>
                <div className="text-xs text-[#6B7280] mb-6">View & download your invoices</div>
                <div className="flex flex-col items-center gap-3 py-12 text-center">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400"><ReceiptText className="w-5 h-5" /></div>
                  <p className="text-sm text-[#6B7280]">No invoices yet. Your first invoice will appear here after your first payment.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Change payment panel */}
        {panel === 'payment' && (
          <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black/30" onClick={() => setPanel(null)} />
            <div className="w-full max-w-sm bg-white shadow-2xl flex flex-col">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <button onClick={() => setPanel(null)} className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-gray-900">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={() => setPanel(null)}><X className="w-4 h-4 text-gray-400" /></button>
              </div>
              <div className="px-6 py-5 flex-1 overflow-y-auto">
                <div className="text-sm font-semibold text-[#111827] mb-1">Change payment method</div>
                <div className="text-xs text-[#6B7280] mb-6">Update your card and billing address.</div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Card number</label>
                    <div className="relative">
                      <input type="text" inputMode="numeric" maxLength={19} value={cardNum}
                        onChange={e => setCardNum(e.target.value.replace(/\D/g, '').slice(0, 16))}
                        placeholder="•••• •••• •••• ••••"
                        className={inputCls} />
                      {cardNum && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500">
                          {detectBrand(cardNum)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Cardholder name</label>
                    <input type="text" value={cardHolder} onChange={e => setCardHolder(e.target.value)} placeholder="Name on card" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Expiry (MM/YY)</label>
                    <input type="text" value={cardExpiry} maxLength={5}
                      onChange={e => {
                        const raw = e.target.value.replace(/\D/g, '');
                        setCardExpiry(raw.length > 2 ? `${raw.slice(0, 2)}/${raw.slice(2)}` : raw);
                      }}
                      placeholder="MM/YY" className={inputCls} />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-4 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 flex-shrink-0" /> Card details are processed securely. Conncct never stores your full card number.
                </p>
                {cardSaved && <p className="text-sm text-green-600 font-medium mt-3 flex items-center gap-1"><Check className="w-4 h-4" /> Saved</p>}
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
                <button onClick={() => setPanel(null)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-[#374151] border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button onClick={handleSaveCard} disabled={savingCard}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition disabled:opacity-50"
                  style={{ background: ACCENT }}
                  onMouseEnter={e => { e.currentTarget.style.background = ACCENT_DEEP; }}
                  onMouseLeave={e => { e.currentTarget.style.background = ACCENT; }}>
                  {savingCard ? 'Saving…' : 'Save card'}
                </button>
              </div>
            </div>
          </div>
        )}

      <DeleteAccountModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
      />
    </AppShell>
  );
}
